// MCHD Room Scheduler - Holiday Management Page
// Comprehensive holiday management interface with modern UI/UX

import { useState, useMemo } from 'react';
import {
  Container,
  Title,
  Text,
  Stack,
  Grid,
  Paper,
  Group,
  Button,
  Select,
  TextInput,
  Badge,
  Table,
  ActionIcon,
  Menu,
  Modal,
  Tabs,
  Card,
  Flex,
  Box,
  Alert,
  Pagination,
  Loader,
  Tooltip,
} from '@mantine/core';
import {
  IconCalendar,
  IconPlus,
  IconSearch,
  IconDots,
  IconEdit,
  IconTrash,
  IconEye,
  IconBuilding,
  IconCalendarEvent,
  IconTrendingUp,
  IconClock,
  IconInfoCircle,
  IconRefresh,
} from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';
import { useHolidays, useHolidayStats, useCreateHoliday, useUpdateHoliday, useDeleteHoliday, useBuildings } from '../../../hooks/admin/useHolidays';
import { HolidayForm } from '../../../components/forms/HolidayForm';
import { HolidayCalendar } from '../../../components/organisms/HolidayCalendar';
import { notifications } from '@mantine/notifications';
import { format, parseISO } from 'date-fns';

// =============================================================================
// TYPES
// =============================================================================

interface HolidayManagementProps {}

interface HolidayFormData {
  buildingId: string;
  dateDay: string;
  label: string;
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

/**
 * Comprehensive Holiday Management Component
 * Features:
 * - Modern dashboard with statistics
 * - Interactive calendar view
 * - Holiday list with search and filtering
 * - Advanced form for creating/editing holidays
 * - Real-time updates and notifications
 * - Responsive design for all devices
 * - Building-specific holiday management
 */
export function HolidayManagement({}: HolidayManagementProps): JSX.Element {
  // State management
  const [activeTab, setActiveTab] = useState<string | null>('overview');
  const [selectedBuilding, setSelectedBuilding] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  
  // Modal states
  const [createModalOpened, { open: openCreateModal, close: closeCreateModal }] = useDisclosure(false);
  const [editModalOpened, { open: openEditModal, close: closeEditModal }] = useDisclosure(false);
  const [viewModalOpened, { open: openViewModal, close: closeViewModal }] = useDisclosure(false);
  const [selectedHoliday, setSelectedHoliday] = useState<any>(null);

  // Hooks
  const { data: buildingsData, isLoading: buildingsLoading } = useBuildings();
  const { data: holidaysData, isLoading: holidaysLoading, refetch: refetchHolidays } = useHolidays({
    ...(selectedBuilding && { buildingId: selectedBuilding }),
    year: selectedYear,
    month: selectedMonth,
    page: currentPage,
    limit: pageSize,
  });

  const { stats, isLoading: statsLoading } = useHolidayStats(selectedBuilding || undefined);
  const createHolidayMutation = useCreateHoliday();
  const updateHolidayMutation = useUpdateHoliday();
  const deleteHolidayMutation = useDeleteHoliday();

  // Building data
  const buildings = buildingsData?.data || [];

  // Computed values
  const filteredHolidays = useMemo(() => {
    if (!holidaysData?.success || !holidaysData.data) return [];
    
    let filtered = holidaysData.data.holidays;
    
    if (searchQuery) {
      filtered = filtered.filter(holiday =>
        holiday.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
        holiday.building.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    return filtered;
  }, [holidaysData, searchQuery]);

  // Event handlers
  const handleCreateHoliday = async (values: HolidayFormData) => {
    try {
      await createHolidayMutation.mutateAsync(values);
      closeCreateModal();
      refetchHolidays();
    } catch (error) {
      console.error('Error creating holiday:', error);
    }
  };

  const handleEditHoliday = async (values: HolidayFormData) => {
    if (!selectedHoliday) return;
    
    try {
      await updateHolidayMutation.mutateAsync({
        id: selectedHoliday.id,
        data: values,
      });
      closeEditModal();
      refetchHolidays();
    } catch (error) {
      console.error('Error updating holiday:', error);
    }
  };

  const handleDeleteHoliday = async (holidayId: string) => {
    try {
      await deleteHolidayMutation.mutateAsync(holidayId);
      refetchHolidays();
      notifications.show({
        title: 'Holiday Deleted',
        message: 'The holiday has been deleted successfully.',
        color: 'green',
      });
    } catch (error) {
      console.error('Error deleting holiday:', error);
    }
  };

  const handleViewHoliday = (holiday: any) => {
    setSelectedHoliday(holiday);
    openViewModal();
  };

  const handleEditHolidayClick = (holiday: any) => {
    setSelectedHoliday(holiday);
    openEditModal();
  };

  // Year options for dropdown
  const yearOptions = Array.from({ length: 10 }, (_, i) => {
    const year = new Date().getFullYear() - 2 + i;
    return { value: year.toString(), label: year.toString() };
  });

  // Month options for dropdown
  const monthOptions = [
    { value: '1', label: 'January' },
    { value: '2', label: 'February' },
    { value: '3', label: 'March' },
    { value: '4', label: 'April' },
    { value: '5', label: 'May' },
    { value: '6', label: 'June' },
    { value: '7', label: 'July' },
    { value: '8', label: 'August' },
    { value: '9', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
  ];

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        {/* Header */}
        <Box>
          <Group gap="md" mb="sm">
            <IconCalendar size={32} color="var(--mantine-color-blue-6)" />
            <Stack gap={0}>
              <Title order={1}>Holiday Management</Title>
          <Text size="lg" c="dimmed">
                Manage hospital holidays and special dates
          </Text>
            </Stack>
          </Group>
        </Box>

        {/* Statistics Cards */}
        {!statsLoading && stats && (
          <Grid>
            <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
              <Card shadow="sm" padding="lg" radius="md" withBorder>
                <Group justify="space-between" mb="xs">
                  <Text size="sm" c="dimmed">Total Holidays</Text>
                  <IconCalendarEvent size={20} color="var(--mantine-color-blue-6)" />
                </Group>
                <Text size="xl" fw={700}>{stats.total}</Text>
              </Card>
            </Grid.Col>
            
            <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
              <Card shadow="sm" padding="lg" radius="md" withBorder>
                <Group justify="space-between" mb="xs">
                  <Text size="sm" c="dimmed">This Year</Text>
                  <IconTrendingUp size={20} color="var(--mantine-color-green-6)" />
                </Group>
                <Text size="xl" fw={700}>{stats.thisYear}</Text>
              </Card>
            </Grid.Col>
            
            <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
              <Card shadow="sm" padding="lg" radius="md" withBorder>
                <Group justify="space-between" mb="xs">
                  <Text size="sm" c="dimmed">This Month</Text>
                  <IconClock size={20} color="var(--mantine-color-orange-6)" />
                </Group>
                <Text size="xl" fw={700}>{stats.thisMonth}</Text>
              </Card>
            </Grid.Col>
            
            <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
        <Card shadow="sm" padding="lg" radius="md" withBorder>
                <Group justify="space-between" mb="xs">
                  <Text size="sm" c="dimmed">Upcoming</Text>
                  <IconCalendar size={20} color="var(--mantine-color-purple-6)" />
                </Group>
                <Text size="xl" fw={700}>{stats.upcoming}</Text>
              </Card>
            </Grid.Col>
          </Grid>
        )}

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab value="overview" leftSection={<IconCalendar size={16} />}>
              Overview
            </Tabs.Tab>
            <Tabs.Tab value="calendar" leftSection={<IconCalendarEvent size={16} />}>
              Calendar View
            </Tabs.Tab>
            <Tabs.Tab value="list" leftSection={<IconEye size={16} />}>
              Holiday List
            </Tabs.Tab>
          </Tabs.List>

          {/* Overview Tab */}
          <Tabs.Panel value="overview" pt="lg">
            <Stack gap="lg">
              <Alert
                icon={<IconInfoCircle size={16} />}
                title="Holiday Management Overview"
                color="blue"
                variant="light"
              >
                <Stack gap="xs">
                  <Text size="sm">
                    • Holidays prevent scheduling on specific dates for each building
                  </Text>
                  <Text size="sm">
                    • Use the Calendar View to visualize holidays across months
                  </Text>
                  <Text size="sm">
                    • Manage individual holidays in the Holiday List tab
                  </Text>
                  <Text size="sm">
                    • Each building can have unique holidays and schedules
            </Text>
          </Stack>
              </Alert>

              <Grid>
                <Grid.Col span={{ base: 12, md: 8 }}>
                  <Paper radius="md" p="lg" withBorder>
                    <Text size="lg" fw={600} mb="md">Quick Actions</Text>
                    <Group gap="md">
                      <Button
                        leftSection={<IconPlus size={16} />}
                        onClick={openCreateModal}
                        variant="light"
                        disabled={!selectedBuilding}
                      >
                        Add Holiday
                      </Button>
                      <Button
                        leftSection={<IconRefresh size={16} />}
                        onClick={() => refetchHolidays()}
                        variant="subtle"
                      >
                        Refresh Data
                      </Button>
                    </Group>
                    {!selectedBuilding && (
                      <Text size="sm" c="dimmed" mt="xs">
                        Please select a building to manage holidays
                      </Text>
                    )}
                  </Paper>
                </Grid.Col>
                
                <Grid.Col span={{ base: 12, md: 4 }}>
                  <Paper radius="md" p="lg" withBorder>
                    <Text size="lg" fw={600} mb="md">Building & Filters</Text>
                    <Stack gap="sm">
                      <Select
                        placeholder="Select Building"
                        data={buildings.map((building: any) => ({
                          value: building.id,
                          label: building.name,
                          description: building.description,
                        }))}
                        value={selectedBuilding || ''}
                        onChange={(value) => setSelectedBuilding(value || null)}
                        leftSection={<IconBuilding size={16} />}
                        searchable
                        clearable
                        size="sm"
                        disabled={buildingsLoading}
                      />
                      <Select
                        placeholder="Select Year"
                        data={yearOptions}
                        value={selectedYear.toString()}
                        onChange={(value) => setSelectedYear(parseInt(value || '2024'))}
                        size="sm"
                        disabled={!selectedBuilding}
                      />
                      <Select
                        placeholder="Select Month"
                        data={monthOptions}
                        value={selectedMonth.toString()}
                        onChange={(value) => setSelectedMonth(parseInt(value || '1'))}
                        size="sm"
                        disabled={!selectedBuilding}
                      />
                    </Stack>
                  </Paper>
                </Grid.Col>
              </Grid>
            </Stack>
          </Tabs.Panel>

          {/* Calendar View Tab */}
          <Tabs.Panel value="calendar" pt="lg">
            <Stack gap="lg">
              {/* Building Selection Header */}
              <Paper radius="md" p="lg" withBorder>
                <Group justify="space-between" align="center">
                  <Stack gap="xs">
                    <Text size="lg" fw={600}>
                      Holiday Calendar
                    </Text>
                    <Text size="sm" c="dimmed">
                      Select a building to view and manage holidays
                    </Text>
                  </Stack>
                  
                  <Group gap="md" align="center">
                    <Select
                      placeholder="Select Building"
                      data={buildings.map((building: any) => ({
                        value: building.id,
                        label: building.name,
                        description: building.description,
                      }))}
                      value={selectedBuilding || ''}
                      onChange={(value) => setSelectedBuilding(value || null)}
                      leftSection={<IconBuilding size={16} />}
                      searchable
                      clearable
                      size="md"
                      disabled={buildingsLoading}
                      style={{ minWidth: 200 }}
                    />
                    
                    {selectedBuilding && (
                      <Button
                        leftSection={<IconPlus size={16} />}
                        onClick={openCreateModal}
                        size="md"
                        variant="filled"
                      >
                        Add Holiday
                      </Button>
                    )}
                  </Group>
                </Group>
              </Paper>

              {/* Calendar Content */}
              {!selectedBuilding ? (
                <Paper radius="md" p="xl" withBorder>
                  <Stack align="center" gap="md">
                    <IconCalendar size={64} color="var(--mantine-color-gray-4)" />
                    <Text size="xl" fw={500} c="dimmed">
                      Select a Building
                    </Text>
                    <Text size="sm" c="dimmed" ta="center" maw={400}>
                      Choose a building from the dropdown above to view its holiday calendar and manage holidays.
                    </Text>
                  </Stack>
                </Paper>
              ) : (
                <HolidayCalendar
                  selectedBuildingId={selectedBuilding}
                  onHolidayClick={handleViewHoliday}
                  onDateClick={(date) => {
                    setSelectedHoliday({
                      dateDay: format(date, 'yyyy-MM-dd'),
                      buildingId: selectedBuilding,
                      label: '',
                    });
                    openCreateModal();
                  }}
                  showActions={true}
                />
              )}
            </Stack>
          </Tabs.Panel>

          {/* Holiday List Tab */}
          <Tabs.Panel value="list" pt="lg">
            <Stack gap="lg">
              {/* Filters and Search */}
              <Paper radius="md" p="lg" withBorder>
                <Group justify="space-between" mb="md">
                  <Text size="lg" fw={600}>Holiday List</Text>
                  <Button
                    leftSection={<IconPlus size={16} />}
                    onClick={openCreateModal}
                    size="sm"
                    disabled={!selectedBuilding}
                  >
                    Add Holiday
                  </Button>
                </Group>
                
                {!selectedBuilding && (
                  <Alert
                    icon={<IconInfoCircle size={16} />}
                    title="Building Required"
                    color="blue"
                    variant="light"
                    mb="md"
                  >
                    Please select a building from the Overview tab to view and manage holidays.
                  </Alert>
                )}
                
                <Grid>
                  <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                    <TextInput
                      placeholder="Search holidays..."
                      leftSection={<IconSearch size={16} />}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      size="sm"
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                    <Select
                      placeholder="Select Year"
                      data={yearOptions}
                      value={selectedYear.toString()}
                      onChange={(value) => setSelectedYear(parseInt(value || '2024'))}
                      size="sm"
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                    <Select
                      placeholder="Select Month"
                      data={monthOptions}
                      value={selectedMonth.toString()}
                      onChange={(value) => setSelectedMonth(parseInt(value || '1'))}
                      size="sm"
                    />
                  </Grid.Col>
                </Grid>
              </Paper>

              {/* Holidays Table */}
              <Paper radius="md" withBorder>
                {holidaysLoading ? (
                  <Flex align="center" justify="center" h={200}>
                    <Stack align="center" gap="md">
                      <Loader size="lg" />
                      <Text c="dimmed">Loading holidays...</Text>
                    </Stack>
                  </Flex>
                ) : filteredHolidays.length === 0 ? (
                  <Flex align="center" justify="center" h={200}>
                    <Stack align="center" gap="md">
                      <IconCalendar size={48} color="var(--mantine-color-gray-4)" />
                      <Text c="dimmed">No holidays found</Text>
                      <Button
                        leftSection={<IconPlus size={16} />}
                        onClick={openCreateModal}
                        variant="light"
                        size="sm"
                      >
                        Add First Holiday
                      </Button>
                    </Stack>
                  </Flex>
                ) : (
                  <>
                    <Table>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>Holiday</Table.Th>
                          <Table.Th>Building</Table.Th>
                          <Table.Th>Date</Table.Th>
                          <Table.Th>Created</Table.Th>
                          <Table.Th>Actions</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {filteredHolidays.map((holiday) => (
                          <Table.Tr key={holiday.id}>
                            <Table.Td>
                              <Text fw={500}>{holiday.label}</Text>
                            </Table.Td>
                            <Table.Td>
                              <Group gap="xs">
                                <IconBuilding size={16} color="var(--mantine-color-blue-6)" />
                                <Text size="sm">{holiday.building.name}</Text>
                              </Group>
                            </Table.Td>
                            <Table.Td>
                              <Badge variant="light" color="blue">
                                {format(parseISO(holiday.dateDay), 'MMM dd, yyyy')}
                              </Badge>
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm" c="dimmed">
                                {format(parseISO(holiday.createdAt), 'MMM dd, yyyy')}
                              </Text>
                            </Table.Td>
                            <Table.Td>
                              <Group gap="xs">
                                <Tooltip label="View Details">
                                  <ActionIcon
                                    variant="subtle"
                                    size="sm"
                                    onClick={() => handleViewHoliday(holiday)}
                                  >
                                    <IconEye size={16} />
                                  </ActionIcon>
                                </Tooltip>
                                <Tooltip label="Edit Holiday">
                                  <ActionIcon
                                    variant="subtle"
                                    size="sm"
                                    onClick={() => handleEditHolidayClick(holiday)}
                                  >
                                    <IconEdit size={16} />
                                  </ActionIcon>
                                </Tooltip>
                                <Menu>
                                  <Menu.Target>
                                    <ActionIcon variant="subtle" size="sm">
                                      <IconDots size={16} />
                                    </ActionIcon>
                                  </Menu.Target>
                                  <Menu.Dropdown>
                                    <Menu.Item
                                      leftSection={<IconTrash size={16} />}
                                      color="red"
                                      onClick={() => handleDeleteHoliday(holiday.id)}
                                    >
                                      Delete Holiday
                                    </Menu.Item>
                                  </Menu.Dropdown>
                                </Menu>
                              </Group>
                            </Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                    
                    {/* Pagination */}
                    {holidaysData?.success && holidaysData.data && holidaysData.data.pagination.totalPages > 1 && (
                      <Group justify="center" mt="lg">
                        <Pagination
                          value={currentPage}
                          onChange={setCurrentPage}
                          total={holidaysData.data.pagination.totalPages}
                          size="sm"
                        />
                      </Group>
                    )}
                  </>
                )}
              </Paper>
            </Stack>
          </Tabs.Panel>
        </Tabs>

        {/* Create Holiday Modal */}
        <Modal
          opened={createModalOpened}
          onClose={closeCreateModal}
          title="Create New Holiday"
          size="lg"
        >
          <HolidayForm
            onSubmit={handleCreateHoliday}
            onCancel={closeCreateModal}
            mode="create"
            isLoading={createHolidayMutation.isPending}
          />
        </Modal>

        {/* Edit Holiday Modal */}
        <Modal
          opened={editModalOpened}
          onClose={closeEditModal}
          title="Edit Holiday"
          size="lg"
        >
          {selectedHoliday && (
            <HolidayForm
              initialValues={selectedHoliday}
              onSubmit={handleEditHoliday}
              onCancel={closeEditModal}
              mode="edit"
              isLoading={updateHolidayMutation.isPending}
            />
          )}
        </Modal>

        {/* View Holiday Modal */}
        <Modal
          opened={viewModalOpened}
          onClose={closeViewModal}
          title="Holiday Details"
          size="md"
        >
          {selectedHoliday && (
            <Stack gap="md">
              <Group>
                <IconCalendarEvent size={24} color="var(--mantine-color-blue-6)" />
                <Stack gap={0}>
                  <Text size="lg" fw={600}>{selectedHoliday.label}</Text>
                  <Text size="sm" c="dimmed">Holiday Details</Text>
                </Stack>
              </Group>
              
              <Paper p="md" withBorder>
                <Stack gap="sm">
                  <Group>
                    <Text size="sm" fw={500} c="dimmed">Building:</Text>
                    <Text size="sm">{selectedHoliday.building.name}</Text>
                  </Group>
                  <Group>
                    <Text size="sm" fw={500} c="dimmed">Date:</Text>
                    <Badge variant="light" color="blue">
                      {format(parseISO(selectedHoliday.dateDay), 'EEEE, MMMM dd, yyyy')}
                    </Badge>
                  </Group>
                  <Group>
                    <Text size="sm" fw={500} c="dimmed">Created:</Text>
                    <Text size="sm">{format(parseISO(selectedHoliday.createdAt), 'MMM dd, yyyy')}</Text>
                  </Group>
                </Stack>
              </Paper>
              
              <Group justify="flex-end">
                <Button
                  variant="light"
                  leftSection={<IconEdit size={16} />}
                  onClick={() => {
                    closeViewModal();
                    handleEditHolidayClick(selectedHoliday);
                  }}
                >
                  Edit Holiday
                </Button>
              </Group>
            </Stack>
          )}
        </Modal>
      </Stack>
    </Container>
  );
}
